import type { FC } from 'react';
import { useEffect, useState } from 'react';

import { useChatSlice } from '../../../store/slices/chat/selectors';
import { ChatTabType } from '../../../store/slices/chat/types';
import { useGameStateSlice } from '../../../store/slices/game/selectors';
import { useHeaderSlice } from '../../../store/slices/header/selectors';
import { useTranslateMessageContent } from '../hooks/useTranslateMessageContent';
import { parseSystemMessage } from '../utils';

import styles from './ChatRibbon.module.css';

const HIDE_DELAY = 3_000;

export const ChatRibbonConnected: FC = () => {
  const [isVisible, setIsVisible] = useState(true);
  const hasWinners = useGameStateSlice.winners().length;
  const isGameChatTabSelected = useChatSlice.getSelectedTab() === ChatTabType.GameChat;
  const isMenuRouteChat = useHeaderSlice.getNavRoute()[1] === 'CHAT';
  const lastMessage = useChatSlice.getLastMessage();
  const lastMessageUser = lastMessage?.userScreenName;
  const lastMessageContent = lastMessage?.content;
  const requireTranslation = lastMessage?.requireTranslation;
  const isSystemMessage = lastMessage?.role === 'SUPPORT' && lastMessage?.sendType === 'TABLE';
  const translatedContent = useTranslateMessageContent(lastMessageContent);

  // Hide component if last message hasn't been updated for 3000ms
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    if (lastMessageContent) {
      setIsVisible(true);
      timeoutId = setTimeout(() => setIsVisible(false), HIDE_DELAY);
    }

    return () => clearTimeout(timeoutId);
  }, [lastMessageContent]);

  if (hasWinners || !isVisible || !lastMessageContent || (isGameChatTabSelected && isMenuRouteChat)) {
    return null;
  }

  const generateMessageContent = (): string => {
    if (isSystemMessage) {
      return parseSystemMessage(lastMessageContent); // Remove HTML tags from system message
    }

    return requireTranslation ? translatedContent : lastMessageContent;
  };

  return (
    <div
      data-testid="chat-ribbon"
      className={styles.chatRibbon}
      aria-label={lastMessageUser ? `${lastMessageUser}:` : ''}
    >
      <div data-testid="chat-ribbon-message" className={styles.winnersList__sliderCnt}>
        {generateMessageContent()}
      </div>
    </div>
  );
};
